import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import './sbnav.scss'
import api from '../../../api'
import { useAuth } from 'react-oidc-context'
import { useAppUser } from '../../../context/UserContext/UserContext'
import { restoreLocalAccountId } from '../../../context/clientIdHelper'
import { queueAuthNotice } from '../../../components/AuthToast/AuthToast'
import useCloseOnClickAway from '../../hooks/UseCloseOnClickAway'
import OptionsIcon from '../svgIcons/OptionsIcon'



const ANONYMOUS_ROOM_LIMIT = 9;
const ACCOUNT_ROOM_LIMIT = 12;
const ACCOUNT_NAME_KEY = 'flashroom-accountName';
const ACCOUNT_EMAIL_KEY = 'flashroom-accountEmail';
const REMEMBERED_ACCOUNT_NAME_KEY = 'flashroom-rememberedAccountName';
const REMEMBERED_ACCOUNT_EMAIL_KEY = 'flashroom-rememberedAccountEmail';
const ACCOUNT_CONTINUE_DISMISSED_KEY = 'flashroom-account-continue-dismissed-at';

const SBNav = () => {
    const auth = useAuth();
    const location = useLocation();

    const { user, loading, error } = useAppUser();

    const [roomCount, setRoomCount] = useState(0);
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const [logoutProcessing, setLogoutProcessing] = useState(false);
    const accountMenuRef = useRef<HTMLDivElement | null>(null);
    const logoutModalRef = useRef<HTMLDivElement | null>(null);

    // const flashroomName = localStorage.getItem("flashroom-accountName");
    const flashroomName = user?.displayName


    const nameLetter: string =
        flashroomName?.trim().charAt(0).toUpperCase() ?? "";
    const roomLimit = auth.isAuthenticated ? ACCOUNT_ROOM_LIMIT : ANONYMOUS_ROOM_LIMIT;
    const hasOwnerBypass = user?.plan === 'flashroom-owner';
    const shouldApplyRoomLimit = !hasOwnerBypass && !(auth.isAuthenticated && loading);
    const isWorkspaceNav = location.pathname.startsWith('/room/');

    const getRoomCount = useCallback(async () => {
        if (auth.isLoading) return;

        try {
            if (auth.isAuthenticated) {
                if (!auth.user?.access_token) return;

                const res = await api.get('/rooms/account/owned', {
                    headers: { Authorization: `Bearer ${auth.user.access_token}` },
                });
                setRoomCount(res.data.rooms.length);
                return;
            }

            const clientId = localStorage.getItem("flashroom_id");
            if (!clientId) return
            const res = await api.get(`rooms/byclient/${clientId}`)

            const roomsCount = res.data.rooms.length;

            setRoomCount(roomsCount);
        } catch (err) {
        }
    }, [auth.isAuthenticated, auth.isLoading, auth.user?.access_token])

    const handleLogin = () => {
        queueAuthNotice('login');
        void auth.signinRedirect({
            extraQueryParams: {
                prompt: 'select_account',
            },
        });
    };





    // const handleLogout = async () => {
    //     const cognitoClientId = "7kcc1dsanbusfhm6bfhc643edr";

    //     const logoutUri =
    //         window.location.hostname === "localhost"
    //             ? "http://localhost:3000/"
    //             : "https://flash-room.com/";

    //     const params = new URLSearchParams({
    //         client_id: cognitoClientId,
    //         logout_uri: logoutUri,
    //     });


    //     await auth.removeUser();


    //     localStorage.removeItem("flashroom_id");
    //     localStorage.removeItem("flashroom-accountName");


    //     window.location.assign(
    //         `https://auth.flash-room.com/logout?${params.toString()}`
    //     );
    // };


    useCloseOnClickAway({
        targetRef: accountMenuRef,
        onClose: () => setAccountMenuOpen(false),
    });

    useCloseOnClickAway({
        targetRef: logoutModalRef,
        onClose: () => {
            if (!logoutProcessing) setLogoutConfirmOpen(false);
        },
    });

    const handleLogout = async () => {
        if (logoutProcessing) return;
        setLogoutProcessing(true);
        const logoutUri = `${window.location.origin}/`;

        const params = new URLSearchParams({
            client_id: '7kcc1dsanbusfhm6bfhc643edr',
            logout_uri: logoutUri,
        });

        queueAuthNotice('logout');

        const profile = auth.user?.profile as Record<string, unknown> | undefined;
        const rememberedEmail =
            (typeof profile?.email === 'string' ? profile.email.trim() : '') ||
            localStorage.getItem(ACCOUNT_EMAIL_KEY)?.trim();
        const rememberedName =
            user?.displayName?.trim() ||
            localStorage.getItem(ACCOUNT_NAME_KEY)?.trim() ||
            rememberedEmail;

        if (rememberedEmail) {
            localStorage.setItem(REMEMBERED_ACCOUNT_EMAIL_KEY, rememberedEmail);
            localStorage.setItem(REMEMBERED_ACCOUNT_NAME_KEY, rememberedName || rememberedEmail);
            localStorage.removeItem(ACCOUNT_CONTINUE_DISMISSED_KEY);
        }

        // Clears react-oidc-context's stored authenticated user.
        await auth.removeUser();
        localStorage.removeItem('flashroom_roomToken');
        localStorage.removeItem('flashroom_most_recent');
        localStorage.removeItem('flashroom_id');
        restoreLocalAccountId();
        localStorage.removeItem('flashroom-accountId');
        localStorage.removeItem(ACCOUNT_NAME_KEY);
        localStorage.removeItem(ACCOUNT_EMAIL_KEY);
        localStorage.removeItem('flashroom-accountClientId');

        window.location.href =
            `https://auth.flash-room.com/logout?${params.toString()}`;
    };



    useEffect(() => {
        getRoomCount();
    }, [getRoomCount])


    return (
        <div className={`sidebar-nav ${isWorkspaceNav ? 'workspace-nav' : 'force-dark-nav'}`}>

            {auth.isAuthenticated ? (
                loading ? (
                    <div className="account-pill">
                        <span className="account-pill-email">Loading account...</span>
                    </div>
                ) : user ? (
                    <div className="account-pill">
                        <div className="name-letter-circle">
                            {nameLetter}
                        </div>

                        <div className="account-pill-info">
                            <span className="account-pill-email">
                                {user.email || "Email unavailable"}
                            </span>
                        </div>

                        <div className="account-pill-menu" ref={accountMenuRef}>
                            <button
                                type="button"
                                className="account-pill-options-button"
                                aria-label="Account options"
                                aria-expanded={accountMenuOpen}
                                onClick={() => setAccountMenuOpen((open) => !open)}
                            >
                                <span className="account-options-icon" aria-hidden="true">
                                    <OptionsIcon />
                                </span>
                            </button>

                            {accountMenuOpen && (
                                <div className="account-options-popup">
                                    <Link
                                        className="account-options-item"
                                        to="/rooms"
                                        onClick={() => setAccountMenuOpen(false)}
                                    >
                                        Your Rooms
                                    </Link>

                                    <button
                                        type="button"
                                        className="account-options-item danger"
                                        onClick={() => {
                                            setAccountMenuOpen(false);
                                            setLogoutConfirmOpen(true);
                                        }}
                                    >
                                        Log out
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="account-pill">
                        <span className="account-pill-email">
                            {error || "Unable to load account"}
                        </span>
                    </div>
                )
            ) : (
                <button
                    type="button"
                    className="sign-in-options"
                    onClick={handleLogin}
                >
                    <span className="sign-in-icon" aria-hidden="true">
                        <svg
                            viewBox="0 0 198.61 177.3"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="99.31" cy="55.45" r="55.45" />
                            <path d="M99.31,118.37C44.46,118.37,0,143.98,0,175.57h0c.02.58.06,1.16.11,1.73l198.42-.67c.03-.3.06-.61.08-.91v-.34c-.18-31.52-44.58-57.01-99.3-57.01h0Z" />
                        </svg>
                    </span>

                    <span className="sign-in-copy">
                        <span className="sign-in-title">Sign in or create account</span>
                        <span className="sign-in-subtitle">Save rooms and access them anywhere</span>
                    </span>

                    <span className="sign-in-arrow" aria-hidden="true">
                        ›
                    </span>
                </button>
            )}



            <ul>
                <li><Link className="nav-sb-link" to='/'>Home</Link></li>
                <li><Link className="nav-sb-link" to='/rooms'>Rooms</Link></li>

                {!shouldApplyRoomLimit || roomCount < roomLimit ? (
                    <li><Link className="nav-sb-link" to='/createroom'>Create Room</Link></li>
                ) : (
                    <li><Link className="nav-sb-link" to='/rooms'>Create Room</Link></li>

                )}



                <li><Link className="nav-sb-link" to='/join'>Join Room</Link></li>

                <li><Link className="nav-sb-link" to='/about'>About Flashroom</Link></li>

                {/* <li><Link className="nav-sb-link" to='/howto'>How To</Link></li> */}





            </ul>


            <div className='sbnav-bottom'>
                <Link className="nav-sb-link" to='/terms'>Terms and Conditions</Link>

                <Link className="nav-sb-link" to='/privacy'>Privacy Policy</Link>
            </div>

            {logoutConfirmOpen && (
                <div className="flashroom-modal-backdrop" role="presentation">
                    <div
                        ref={logoutModalRef}
                        className="flashroom-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="logout-confirm-title"
                    >
                        <h2 id="logout-confirm-title">Log out of FlashRoom?</h2>
                        <p className="logout-modal-copy">You will be redirected to the homepage.</p>

                        <div className="logout-modal-actions">
                            <button
                                type="button"
                                className="logout-cancel-button"
                                disabled={logoutProcessing}
                                onClick={() => setLogoutConfirmOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="logout-confirm-button"
                                disabled={logoutProcessing}
                                onClick={handleLogout}
                            >
                                {logoutProcessing ? 'Logging out...' : 'Log out'}
                            </button>
                        </div>
                    </div>
                </div>
            )}


        </div>
    )
}

export default SBNav
